import { AsyncPipe, isPlatformBrowser } from '@angular/common';
import { Component, inject, LOCALE_ID, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
	AccountDisabledComponent,
	AuthBffService,
	CompanySwitcherService,
} from '@visiativ/digital-account-front';
import { PageTitleService, StoreService } from '@visiativ/digital-components-kit';
import { UikBrandingService, UikSpinnerComponent } from '@visiativ/uik-am';
import { UikBrandColors, UikColor } from '@visiativ/uik-core';
import { productFruits } from 'product-fruits';
import { combineLatest, filter, map, Subject, startWith, takeUntil } from 'rxjs';
import { environment } from '../environments/environment';
import { Page } from './portal/interfaces/page.interface';
import { PortalComponent } from './portal/portal.component';

interface AxeptioWindow extends Window {
	_axcb?: Array<(sdk: any) => void>;
}

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [
		AccountDisabledComponent,
		AsyncPipe,
		RouterModule,
		PortalComponent,
		UikSpinnerComponent,
	],
})
export class AppComponent implements OnDestroy, OnInit {
	private readonly router = inject(Router);
	private readonly titleService = inject(Title);
	private readonly pageTitleService = inject(PageTitleService);
	public readonly storeService = inject(StoreService);
	private readonly unsubscribeSubject = new Subject<void>();
	public readonly companySwitcher = inject(CompanySwitcherService);
	public readonly auth = inject(AuthBffService);
	private readonly uikBrandingService: UikBrandingService = inject(UikBrandingService);

	private readonly platformId = inject(PLATFORM_ID);
	private readonly locale: string = inject(LOCALE_ID);

	private productFruitsInitialized = false;
	private hadConsent = false;
	private chatConsentCategory = 'product-fruits';

	public readonly isInviteRoute = toSignal(
		this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map(event => event.urlAfterRedirects.startsWith('/invite')),
		),
		{ initialValue: this.router.url.startsWith('/invite') },
	);

	private readonly pages = [
		{
			title: $localize`:@@hub.global.home-label:Accueil`,
			url: '/home',
		},
		{
			title: 'Loading...',
			url: '/logged',
		},
		{
			title: 'Loading...',
			url: '/logout',
		},
		{
			title: $localize`:@@hub.page.change-password-title:Changement du mot de passe`,
			url: '/change-password',
		},
		{
			title: $localize`:@@hub.global.my-information-label:Mes informations`,
			url: '/my_information',
		},
		{
			title: $localize`:@@hub.global.client-service-label:Service client`,
			url: '/helpdesk',
			subPages: [
				{
					title: $localize`:@@hub.global.helpdesk-label:Mes demandes d'assistance`,
					url: '/helpdesk',
				},
				{
					title: $localize`:@@hub.global.flexo-project-label:Projets Flexo`,
					url: '/projects/flexo',
				},
				{
					title: $localize`:@@hub.global.download-center-label:Centre de téléchargement`,
					url: '/download-center',
				},
			],
		},
		{
			title: $localize`:@@hub.global.my-company-label:Ma société`,
			url: '/my_company',
			subPages: [
				{
					title: $localize`:@@hub.global.company-information-label:Informations société`,
					url: '/my_company',
				},
				{
					title: $localize`:@@hub.global.users-label:Utilisateurs`,
					url: '/users',
				},
				{
					title: $localize`:@@hub.global.licences-label:Licences`,
					url: '/licences',
				},
				{
					title: $localize`:@@hub.global.contracts-label:Contrats`,
					url: '/contracts',
				},
			],
		},
		{
			title: $localize`:@@hub.global.my-projects-label:Projets`,
			url: '/projects',
		},
		{
			title: $localize`:@@digital-i18n.menu.orders-label:Commandes`,
			url: '/orders',
		},
		{
			title: $localize`:@@hub.global.training-label:Formation`,
			url: '/training/catalog',
			subPages: [
				{
					title: $localize`:@@hub.global.training-calendar-label:Calendrier des formations`,
					url: '/training/calendar',
				},
				{
					title: '',
					url: '/training/course',
				},
				{
					title: $localize`:@@hub.global.training-catalog-label:Toutes les formations`,
					url: '/training/catalog',
				},
				{
					title: $localize`:@@hub.global.training-my-sessions-label:Mes sessions`,
					url: '/my-sessions',
				},
			],
		},
		{
			title: $localize`:@@hub.global.communities-label:Communautés`,
			url: '/my_communities',
		},
		{
			title: $localize`:@@hub.global.legal-documents-label:Documents légaux`,
			url: '/legal_documents',
		},
	];

	ngOnInit(): void {
		this.configurePageTitle();
		const brandColors: UikBrandColors = {
			colorDefs: [
				{ name: 'primary', color: UikColor.fromHex('#454cff') },
				{ name: 'accent', color: UikColor.fromHex('#34beae') },
			],
		};

		this.uikBrandingService.applyBranding({ brandColors });

		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const axeptioWindow = window as AxeptioWindow;

		axeptioWindow._axcb = axeptioWindow._axcb || [];
		axeptioWindow._axcb.push((sdk: any) => {
			sdk.on('cookies:complete', (choices: Record<string, boolean>) => {
				const accepted = Boolean(choices[this.chatConsentCategory]);

				if (!accepted) {
					// If user had accepted before and now revoked, reload to fully disable runtime state
					if (this.hadConsent) {
						window.location.reload();
					}
					return;
				}

				this.hadConsent = true;

				if (!this.productFruitsInitialized) {
					productFruits.init(environment.workspaceID, this.locale, {
						username:
							this.storeService.connectedUser?.firstname +
							' ' +
							this.storeService.connectedUser?.lastname,
						role: this.storeService.connectedUser?.role?.toString(),
					});
					this.productFruitsInitialized = true;
				}
			});
		});
	}

	ngOnDestroy(): void {
		this.unsubscribeSubject.next();
		this.unsubscribeSubject.complete();
	}

	private findPageTitleByUrl(url: string, pages: Page[]): string {
		if (url.endsWith('support')) {
			return $localize`:@@hub.page.no-company-access-title:Vérification entreprise`;
		}

		const pageMatch = this.findMatchingPage(url, pages);
		if (!pageMatch) {
			return this.getDefaultPageTitle(url);
		}

		if (pageMatch.subPageTitle) {
			return `${pageMatch.page.title} - ${pageMatch.subPageTitle}`;
		}
		return pageMatch.page.title;
	}

	private findMatchingPage(
		url: string,
		pages: Page[],
	): { page: Page; subPageTitle?: string } | null {
		for (const page of pages) {
			const result = this.matchPage(url, page);
			if (result) {
				return result;
			}
		}
		return null;
	}

	private matchPage(url: string, page: Page): { page: Page; subPageTitle?: string } | null {
		if (url.startsWith(page.url)) {
			const subPageTitle = this.extractSubPageTitle(url, page);
			return subPageTitle ? { page, subPageTitle } : { page };
		}
		const subPageTitle = this.extractSubPageTitle(url, page);
		return subPageTitle ? { page, subPageTitle } : null;
	}

	private extractSubPageTitle(url: string, page: Page): string | undefined {
		if (!Array.isArray(page.subPages) || page.subPages.length === 0) {
			return undefined;
		}
		const subTitle = this.findSubPageTitle(url, page.subPages);
		return subTitle && subTitle !== page.title ? subTitle : undefined;
	}

	private findSubPageTitle(url: string, subPages: Page[]): string | undefined {
		for (const subPage of subPages) {
			if (url.startsWith(subPage.url)) {
				if (Array.isArray(subPage.subPages) && subPage.subPages.length > 0) {
					const subTitle = this.findSubPageTitle(url, subPage.subPages);
					if (subTitle && subTitle !== subPage.title) {
						return `${subPage.title} - ${subTitle}`;
					}
				}
				return subPage.title;
			}
		}
		return undefined;
	}

	private getDefaultPageTitle(url: string): string {
		if (url === '/page-not-found') {
			return $localize`:@@hub.page.not-found-title:Page introuvable`;
		}
		if (url === '/home') {
			return $localize`:@@hub.global.home-label:Accueil`;
		}
		return 'My Visiativ';
	}

	private configurePageTitle(): void {
		const navigationEnd$ = this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
		);
		combineLatest([navigationEnd$, this.pageTitleService.title$.pipe(startWith(null))])
			.pipe(
				map(([event, injectedTitle]) => {
					const baseTitle = this.findPageTitleByUrl(event.urlAfterRedirects, this.pages);
					if (injectedTitle && baseTitle.split('-').length > 1) {
						const smallTitle = baseTitle.split('-').slice(-1)[0].trim();
						return `${smallTitle} - ${injectedTitle}`;
					}
					return injectedTitle ? `${baseTitle} - ${injectedTitle}` : baseTitle;
				}),
				takeUntil(this.unsubscribeSubject),
			)
			.subscribe(title => {
				this.titleService.setTitle(title);
			});
	}
}
